import React, { useRef, useState } from 'react';
import { Upload, Modal, Input, Button, Typography } from 'antd';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../utils/imageUtils';

const { Dragger } = Upload;
const { Text } = Typography;

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPT = '.jpg,.jpeg,.png,.webp';

const ItemImageUpload = ({
  item,
  index,
  onDrop,
  onRemoveImage,
  onReorderImages,
  onUpdateImageDescription,
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [pasteEnabled, setPasteEnabled] = useState(false);
  const processedUidsRef = useRef(new Set());

  const beforeUpload = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are allowed');
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_SIZE) {
      toast.error('Image must be 10MB or smaller');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleChange = ({ file }) => {
    if (!file || file.status === 'removed') return;

    if (processedUidsRef.current.has(file.uid)) return;
    processedUidsRef.current.add(file.uid);

    const raw = file.originFileObj || file;
    if (!(raw instanceof File || raw instanceof Blob)) return;

    const namedFile =
      raw instanceof File && raw.name
        ? raw
        : new File([raw], `paste-${Date.now()}.png`, {
            type: raw.type || 'image/png',
          });

    onDrop(index, [namedFile]);
  };

  const handleDragStart = (e, imageIndex) => {
    setDraggedIndex(imageIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, targetIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(targetIndex);
  };

  const handleReorderDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== targetIndex && onReorderImages) {
      onReorderImages(index, draggedIndex, targetIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleEditDescription = (imageIndex) => {
    const image = item.images[imageIndex];
    setEditingImageIndex(imageIndex);
    setEditingDescription(image.description || '');
  };

  const handleSaveDescription = () => {
    if (editingImageIndex !== null && onUpdateImageDescription) {
      onUpdateImageDescription(index, editingImageIndex, editingDescription);
    }
    setEditingImageIndex(null);
    setEditingDescription('');
  };

  const handleCancelEdit = () => {
    setEditingImageIndex(null);
    setEditingDescription('');
  };

  const handleZoneFocus = () => setPasteEnabled(true);

  const handleZoneBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setPasteEnabled(false);
    }
  };

  const sharedUploadProps = {
    multiple: true,
    accept: ACCEPT,
    showUploadList: false,
    beforeUpload,
    onChange: handleChange,
    fileList: [],
  };

  return (
    <>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
        Images
      </Text>
      <div
        tabIndex={0}
        onMouseDown={handleZoneFocus}
        onFocus={handleZoneFocus}
        onBlur={handleZoneBlur}
        style={{
          outline: pasteEnabled ? '2px solid #1677ff' : 'none',
          borderRadius: 8,
        }}
      >
        <Dragger
          {...sharedUploadProps}
          pastable={pasteEnabled}
          openFileDialogOnClick={false}
          style={{ padding: '12px 8px' }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
            <InboxOutlined style={{ fontSize: 28 }} />
          </p>
          <p className="ant-upload-text" style={{ fontSize: 13, margin: 0 }}>
            Drag or paste images
            {pasteEnabled ? ' · paste ready' : ''}
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 12, marginTop: 4 }}>
            JPEG, PNG, WebP · max 10MB
          </p>
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ marginTop: 6 }}
          >
            <Upload {...sharedUploadProps}>
              <Button size="small" icon={<UploadOutlined />}>
                Select files
              </Button>
            </Upload>
          </div>
        </Dragger>
      </div>

      <div
        className="mt-3 d-flex flex-wrap gap-3"
        style={{ rowGap: '16px', columnGap: '16px' }}
      >
        {item.images.map((image, imageIndex) => (
          <div
            key={image.image_id || imageIndex}
            className="position-relative d-flex flex-column align-items-center shadow-sm"
            style={{
              width: '120px',
              minHeight: '180px',
              border:
                dragOverIndex === imageIndex
                  ? '3px dashed #007bff'
                  : '1px solid #ddd',
              borderRadius: '8px',
              background: dragOverIndex === imageIndex ? '#e3f2fd' : '#fafbfc',
              padding: '8px',
              boxSizing: 'border-box',
              position: 'relative',
              opacity: draggedIndex === imageIndex ? 0.5 : 1,
              transform: draggedIndex === imageIndex ? 'rotate(5deg)' : 'none',
              transition: 'all 0.2s ease',
            }}
            draggable
            onDragStart={(e) => handleDragStart(e, imageIndex)}
            onDragOver={(e) => handleDragOver(e, imageIndex)}
            onDrop={(e) => handleReorderDrop(e, imageIndex)}
            onDragEnd={handleDragEnd}
            onDragLeave={handleDragLeave}
          >
            <div
              style={{
                width: '100%',
                height: '24px',
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#888',
                fontSize: '0.9rem',
                borderBottom: '1px dashed #ccc',
                marginBottom: '4px',
                userSelect: 'none',
              }}
            >
              <span role="img" aria-label="drag">
                ↕️
              </span>{' '}
              Drag
            </div>
            <img
              src={image.preview || getImageUrl(image.image_url)}
              alt="Preview"
              style={{
                width: '100%',
                height: '80px',
                objectFit: 'cover',
                borderRadius: '4px',
                marginBottom: '6px',
              }}
            />
            <div
              style={{
                fontSize: '0.95rem',
                color: '#333',
                marginBottom: '6px',
                textAlign: 'center',
                wordBreak: 'break-word',
                minHeight: '20px',
              }}
            >
              {image.description || (
                <span style={{ color: '#aaa' }}>No description</span>
              )}
            </div>
            <div className="d-flex justify-content-center gap-2 w-100">
              <Button
                type="default"
                size="small"
                onClick={() => handleEditDescription(imageIndex)}
              >
                Edit
              </Button>
              <Button
                danger
                size="small"
                onClick={() => onRemoveImage(index, imageIndex)}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Modal
        title="Edit Image Description"
        open={editingImageIndex !== null}
        onOk={handleSaveDescription}
        onCancel={handleCancelEdit}
        okText="Save"
        destroyOnHidden
      >
        <Input
          value={editingDescription}
          onChange={(e) => setEditingDescription(e.target.value)}
          placeholder="Image description"
          onPressEnter={handleSaveDescription}
        />
      </Modal>
    </>
  );
};

export default ItemImageUpload;
